import { PricingQuoteIncludedProductDto, ProductTypeEnum } from '@inosio/payments-sdk';
import { TableContainer, Table, TableHead, TableRow, TableCell, Typography, TableBody } from '@mui/material';
import NumberFormat from 'react-number-format';

export interface PricingQuoteMonthlyFeesLineItem {
  label: string;
  amount: number;
}
export interface PricingQuoteMonthlyFeesTableProps {
  /**
   * A list of products included in this quote.
   */
  includedProducts: PricingQuoteIncludedProductDto[];

  /**
   * The discount on this quote.
   */
  packageDiscount?: number;
}

const PricingQuoteMonthlyFeesTable = (props: PricingQuoteMonthlyFeesTableProps) => {
  const buildDataForMonthlyFeesTable = (includedProducts: PricingQuoteIncludedProductDto[]) => {
    const monthlyFeeLineItems: PricingQuoteMonthlyFeesLineItem[] = [];

    //get all products which have non zero FlatFee
    const monthlyBilledProducts = includedProducts.filter((prod) => !!prod.FlatFee);

    const tenantPortalMonthlyBilledProduct = monthlyBilledProducts?.find((prod) => prod.ProductType === ProductTypeEnum.TenantPortal);
    const ownerPortalMonthlyBilledProduct = monthlyBilledProducts?.find((prod) => prod.ProductType === ProductTypeEnum.OwnerPortal);
    const appPortalMonthlyBilledProduct = monthlyBilledProducts?.find((prod) => prod.ProductType === ProductTypeEnum.Applications);
    const maintenanceMonthlyBilledProduct = monthlyBilledProducts?.find((prod) => prod.ProductType === ProductTypeEnum.Maintenance);

    if (!!tenantPortalMonthlyBilledProduct) {
      const lineItem: PricingQuoteMonthlyFeesLineItem = {
        label: 'Tenant Portal',
        amount: (tenantPortalMonthlyBilledProduct.FlatFee || 0) / 100,
      };
      monthlyFeeLineItems.push(lineItem);
    }

    if (!!ownerPortalMonthlyBilledProduct) {
      const lineItem: PricingQuoteMonthlyFeesLineItem = {
        label: 'Owner Portal',
        amount: (ownerPortalMonthlyBilledProduct.FlatFee || 0) / 100,
      };
      monthlyFeeLineItems.push(lineItem);
    }
    if (!!appPortalMonthlyBilledProduct) {
      const lineItem: PricingQuoteMonthlyFeesLineItem = {
        label: 'Application Portal',
        amount: (appPortalMonthlyBilledProduct.FlatFee || 0) / 100,
      };
      monthlyFeeLineItems.push(lineItem);
    }
    if (!!maintenanceMonthlyBilledProduct) {
      const lineItem: PricingQuoteMonthlyFeesLineItem = {
        label: 'Maintenance Coordination',
        amount: (maintenanceMonthlyBilledProduct.FlatFee || 0) / 100,
      };
      monthlyFeeLineItems.push(lineItem);
    }
    if (!!props.packageDiscount) {
      const lineItem: PricingQuoteMonthlyFeesLineItem = {
        label: 'Discount*',
        amount: -(props.packageDiscount || 0) / 100,
      };
      monthlyFeeLineItems.push(lineItem);
    }

    return monthlyFeeLineItems;
  };

  return (
    <TableContainer>
      <Table
        sx={{
          borderCollapse: 'collapse', // Ensures borders collapse
          '& th, & td': {
            border: '1px solid #E0E0E0', // Vertical and horizontal borders
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography sx={{ fontWeight: 'bold', fontFamily: 'Work Sans !important' }}>Item</Typography>
            </TableCell>
            <TableCell>
              <Typography sx={{ fontWeight: 'bold', fontFamily: 'Work Sans !important' }}>Price</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {buildDataForMonthlyFeesTable(props.includedProducts).map((item, index) => (
            <TableRow key={index}>
              <TableCell>
                <Typography sx={{ fontFamily: 'Work Sans !important' }}>{item.label}</Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ fontFamily: 'Work Sans !important' }}>
                  <NumberFormat
                    thousandsGroupStyle="thousand"
                    value={item.amount}
                    prefix="$"
                    decimalSeparator="."
                    displayType="text"
                    type="text"
                    thousandSeparator={true}
                    allowNegative={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                  />
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PricingQuoteMonthlyFeesTable;
